import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiCalendar, FiTag } from 'react-icons/fi';
import { getCoupons, createCoupon, updateCoupon, deleteCoupon, getCouponStats } from '../services/coupons';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import CouponForm from '../components/Coupons/CouponForm';
import toast from 'react-hot-toast';

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [filters, setFilters] = useState({ status: '', search: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCoupons();
    fetchStats();
  }, [filters]);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const data = await getCoupons(filters);
      setCoupons(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getCouponStats();
      setStats(data.stats);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  };

  const handleCreate = async (couponData) => {
    try {
      await createCoupon(couponData);
      toast.success('Coupon created successfully');
      setShowForm(false);
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error('Failed to create coupon');
    }
  };

  const handleUpdate = async (id, couponData) => {
    try {
      await updateCoupon(id, couponData);
      toast.success('Coupon updated successfully');
      setEditingCoupon(null);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await deleteCoupon(id);
      toast.success('Coupon deleted successfully');
      fetchCoupons();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isActive = (coupon) => {
    const now = new Date();
    return coupon.is_active && new Date(coupon.start_date) <= now && new Date(coupon.end_date) >= now;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Coupons & Discounts</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          <FiPlus className="mr-2" /> Create Coupon
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Coupons</p>
                <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-full"><FiTag className="text-blue-600" /></div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Coupons</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-full"><FiTag className="text-green-600" /></div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Used</p>
                <p className="text-2xl font-bold text-gray-800">{stats.totalUsage}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-full"><FiCalendar className="text-purple-600" /></div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Savings</p>
                <p className="text-2xl font-bold text-orange-600">${stats.totalSavings}</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-full"><FiTag className="text-orange-600" /></div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Coupon code"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Coupons Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {coupons.map((coupon) => (
                  <tr key={coupon._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-mono font-bold text-primary-600">{coupon.code}</p>
                        <p className="text-xs text-gray-500">{coupon.name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium">
                        {coupon.discount_type === 'percentage' 
                          ? `${coupon.discount_value}%` 
                          : `$${coupon.discount_value}`}
                      </span>
                      {coupon.min_purchase > 0 && (
                        <p className="text-xs text-gray-500">Min: ${coupon.min_purchase}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div>
                        <p>{formatDate(coupon.start_date)}</p>
                        <p className="text-gray-500">→ {formatDate(coupon.end_date)}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {coupon.total_used} / {coupon.usage_limit || '∞'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${isActive(coupon) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {isActive(coupon) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setEditingCoupon(coupon)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(coupon._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {coupons.length === 0 && (
            <div className="text-center py-12 text-gray-500">No coupons found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Coupon Form Modal */}
      <CouponForm
        isOpen={showForm || !!editingCoupon}
        onClose={() => {
          setShowForm(false);
          setEditingCoupon(null);
        }}
        onSubmit={editingCoupon ? (data) => handleUpdate(editingCoupon._id, data) : handleCreate}
        initialData={editingCoupon}
      />
    </div>
  );
}
